import type { WorkflowReadResponse } from '@services/api/aiworkflows'

/**
 * Merge a workflow GET/refetch payload into the React Query cache without regressing
 * lock_version or canvas state when an in-flight poll returns a stale snapshot.
 */
export function mergeWorkflowDetailWithCache(
  cached: WorkflowReadResponse | undefined,
  server: WorkflowReadResponse
): WorkflowReadResponse {
  if (!cached) return server

  const cachedLv = cached.lock_version ?? 1
  const serverLv = server.lock_version ?? 1

  if (serverLv > cachedLv) {
    return { ...server, lock_version: serverLv }
  }

  // Restore/archive retarget current_version_id without bumping lock_version.
  // Take the server graph so the canvas does not keep the previous version's nodes.
  if (
    serverLv === cachedLv &&
    (cached.current_version_id ?? null) !== (server.current_version_id ?? null)
  ) {
    return { ...server, lock_version: serverLv }
  }

  // Equal lock_version: the server has not advanced past what the client holds.
  // A poll that started before/around an autosave can echo the pre-save graph at
  // the same lock_version. Trust the cached graph/trigger here (the canvas owns
  // in-flight state) while still adopting any non-graph server fields. This
  // prevents the canvas from snapping back to a stale graph on the poll cadence.
  if (serverLv === cachedLv) {
    return {
      ...server,
      lock_version: cachedLv,
      graph_definition: cached.graph_definition ?? server.graph_definition,
      trigger_config: cached.trigger_config ?? server.trigger_config,
      execution_config: cached.execution_config ?? server.execution_config,
      updated_at: cached.updated_at ?? server.updated_at,
      name: cached.name ?? server.name,
      description: cached.description ?? server.description,
      icon_file_id: cached.icon_file_id ?? server.icon_file_id,
      tags: cached.tags ?? server.tags,
    }
  }

  // Stale server snapshot (poll started before autosave landed) — keep fresher client state.
  return {
    ...server,
    lock_version: cachedLv,
    updated_at: cached.updated_at ?? server.updated_at,
    graph_definition: cached.graph_definition ?? server.graph_definition,
    trigger_config: cached.trigger_config ?? server.trigger_config,
    execution_config: cached.execution_config ?? server.execution_config,
    name: cached.name,
    description: cached.description,
    icon_file_id: cached.icon_file_id,
    tags: cached.tags,
  }
}
